// =============================================================================
// chartBuilder.js
// Production-grade Apache ECharts option builder for markdown tables.
//
// Design goals:
//   - Robust number parsing: "$50B", "$50–56 billion", "1,250+", "38.9%",
//     "12-18 months", "~50", ranges, magnitudes (T/B/M/K), currencies.
//   - Context-aware default chart type selection (time-series, composition,
//     comparison, etc.) — never guesses something that doesn't fit the data.
//   - Magnitude normalization per column so axes/tooltips show consistent units.
//   - Only exposes chart types that genuinely make sense for the data
//     (`availableTypes`) so the UI never offers a broken option.
//   - All ECharts options share a brand-aligned palette + typography, custom
//     tooltip formatters with units, axis label formatters, and dataZoom for
//     dense series.
// =============================================================================

// ----- Brand palette (purple-led, color-blind safe ordering) -----------------
export const CHART_PALETTE = [
  "#6B2FA0", // brand purple
  "#3b82f6", // blue
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#06b6d4", // cyan
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#84cc16", // lime
  "#0ea5e9", // sky
];

const TEXT_COLOR = "#374151";
const AXIS_COLOR = "#9ca3af";
const SPLIT_COLOR = "#f1f1f4";
const FONT_FAMILY = "inherit";

// ----- Magnitudes ------------------------------------------------------------
const MAG = { T: 1e12, B: 1e9, M: 1e6, K: 1e3 };

const detectMagnitude = (raw) => {
  const s = String(raw);
  if (/\b(?:trillion|tn)\b/i.test(s) || /\d\s*T\b/.test(s)) return "T";
  if (/\b(?:billion|bn)\b/i.test(s) || /\d\s*B\b/.test(s)) return "B";
  if (/\b(?:million|mn)\b/i.test(s) || /\d\s*M\b/.test(s)) return "M";
  if (/\b(?:thousand)\b/i.test(s) || /\d\s*K\b/.test(s)) return "K";
  return null;
};

// ----- Cell value parsing ----------------------------------------------------
// Returns { base, unit, magnitude, isRange, original } or null when not numeric.
//   base     - canonical numeric value, fully expanded (e.g. "$50B" -> 5e10).
//   unit     - "$" | "%" | "x" | null
//   magnitude- "T" | "B" | "M" | "K" | null  (detected from text)
//   isRange  - true if the cell encoded a range like "12-18".
export const parseCellValue = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^(n\/?a|none|—|–|-+|null|tbd|tba|\?)$/i.test(s)) return null;

  // Strip parenthetical citations like " [1]" or " (Source)" before parsing.
  const cleaned = s.replace(/\[[^\]]*\]|\([^)]*\)/g, " ").trim();

  // Range: "12-18", "$50–56 billion", "12 to 18"
  const rangeMatch = cleaned.match(
    /(-?\d+(?:[.,]\d+)?)\s*(?:[-–—]|to)\s*(-?\d+(?:[.,]\d+)?)/i
  );
  let nums;
  let isRange = false;
  if (rangeMatch) {
    nums = [
      parseFloat(rangeMatch[1].replace(/,/g, "")),
      parseFloat(rangeMatch[2].replace(/,/g, "")),
    ];
    isRange = true;
  } else {
    const numMatch = cleaned.match(/-?\d+(?:[.,]\d+)*/);
    if (!numMatch) return null;
    nums = [parseFloat(numMatch[0].replace(/,/g, ""))];
  }
  if (nums.some((n) => Number.isNaN(n))) return null;

  let value = nums.length === 2 ? (nums[0] + nums[1]) / 2 : nums[0];

  // Unit detection (priority: % > $ > x)
  let unit = null;
  if (/%/.test(cleaned)) unit = "%";
  else if (/\$|\busd\b|€|£|¥/i.test(cleaned)) unit = "$";
  else if (/\d\s*x\b/i.test(cleaned)) unit = "x";

  // Magnitude
  const mag = detectMagnitude(cleaned);
  const base = mag ? value * MAG[mag] : value;

  return { base, unit, magnitude: mag, isRange, original: s };
};

// ----- Markdown table parsing -----------------------------------------------
export const parseMarkdownTable = (tableMarkdown) => {
  if (!tableMarkdown) return null;
  const lines = String(tableMarkdown)
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) return null;

  const rows = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Skip header separator (---|---|---)
    if (i === 1 && /-+/.test(line.replace(/\|/g, ""))) continue;
    const cells = line
      .split("|")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);
    if (cells.length === 0) continue;
    rows.push(cells);
  }
  if (!rows.length) return null;

  const headers = rows[0];
  const body = rows.slice(1);
  if (!headers.length || !body.length) return null;
  return { headers, rows: body };
};

// ----- Axis kind detection ---------------------------------------------------
const TIME_TOKENS = [
  "year",
  "yr",
  "fy",
  "date",
  "month",
  "day",
  "week",
  "quarter",
  "q1",
  "q2",
  "q3",
  "q4",
  "period",
  "time",
];

const detectXKind = (xLabel, xValues) => {
  const labelLower = String(xLabel || "").toLowerCase();
  const labelLooksTime = TIME_TOKENS.some((t) => labelLower.includes(t));

  // Are values numeric or year-like?
  const numericVals = [];
  const yearLike = [];
  for (const v of xValues) {
    const s = String(v).trim();
    const n = parseFloat(s.replace(/,/g, ""));
    if (!Number.isNaN(n)) numericVals.push(n);
    if (/^(?:19|20)\d{2}$/.test(s)) yearLike.push(s);
  }

  const allNumeric =
    xValues.length > 0 && numericVals.length === xValues.length;
  const allYears = xValues.length > 0 && yearLike.length === xValues.length;

  if (allYears || (labelLooksTime && allNumeric)) return "time";
  if (labelLooksTime) return "time";
  if (allNumeric) return "numeric";
  return "categorical";
};

// ----- Per-column display magnitude selection -------------------------------
// Choose a single display magnitude based on the median of base values so
// ECharts axes and tooltips read cleanly (e.g., "$50B" instead of "$5e10").
const pickDisplayMagnitude = (baseValues) => {
  const finite = baseValues.filter((v) => Number.isFinite(v) && v !== 0);
  if (!finite.length) return null;
  const sorted = [...finite].map(Math.abs).sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  if (median >= 1e12) return "T";
  if (median >= 1e9) return "B";
  if (median >= 1e6) return "M";
  if (median >= 1e3) return "K";
  return null;
};

// ----- Number formatters -----------------------------------------------------
const fmtNumber = (n, { unit, magnitude, decimals = 1 }) => {
  if (n == null || Number.isNaN(n)) return "—";
  let v = n;
  let suffix = magnitude || "";
  // Magnitude already applied to display value; we just append the suffix.
  let s;
  if (Math.abs(v) >= 100) s = v.toFixed(0);
  else s = v.toFixed(decimals).replace(/\.0+$/, "");

  if (unit === "$") return `$${s}${suffix}`;
  if (unit === "%") return `${s}%`;
  if (unit === "x") return `${s}×`;
  if (suffix) return `${s}${suffix}`;
  return s;
};

const axisLabelFormatter = (unit, magnitude) => (v) =>
  fmtNumber(v, { unit, magnitude, decimals: 1 });

// ----- Main analyzer ---------------------------------------------------------
// Produces:
//   { xLabel, xValues, xKind,
//     yColumns: [{ key, values, unit, magnitude, allPercentage,
//                  rowsSumNear100, hasNegative }],
//     bestType, availableTypes }
export const buildTableChartConfig = (tableMarkdown) => {
  const parsed = parseMarkdownTable(tableMarkdown);
  if (!parsed) return null;

  const { headers, rows } = parsed;
  if (headers.length < 2) return null;

  const xLabel = headers[0];
  const xValues = rows.map((r) => r[0]);
  const xKind = detectXKind(xLabel, xValues);

  // Parse each candidate y column.
  const yColumns = [];
  for (let i = 1; i < headers.length; i++) {
    const key = headers[i];
    const parsedCells = rows.map((row) => parseCellValue(row[i]));
    const numericCount = parsedCells.filter(Boolean).length;
    if (numericCount < Math.max(1, Math.floor(rows.length / 2))) continue;

    // Pick column-level unit (most frequent non-null unit)
    const unitCounts = {};
    parsedCells.forEach((p) => {
      if (p && p.unit) unitCounts[p.unit] = (unitCounts[p.unit] || 0) + 1;
    });
    const unit =
      Object.keys(unitCounts).sort(
        (a, b) => unitCounts[b] - unitCounts[a]
      )[0] || null;

    // Display magnitude (skip for percentages)
    const baseValues = parsedCells.map((p) => (p ? p.base : null));
    const displayMag =
      unit === "%" ? null : pickDisplayMagnitude(baseValues.filter(Number.isFinite));
    const divisor = displayMag ? MAG[displayMag] : 1;

    const values = baseValues.map((b) =>
      Number.isFinite(b) ? b / divisor : null
    );

    const numericVals = values.filter((v) => typeof v === "number");
    const hasNegative = numericVals.some((v) => v < 0);
    const allPercentage = unit === "%";

    yColumns.push({
      key,
      values,
      unit,
      magnitude: displayMag,
      allPercentage,
      hasNegative,
    });
  }

  if (!yColumns.length) return null;

  // ---- Composition heuristics ----------------------------------------------
  const isCompositionSingle = (col) => {
    if (!col.allPercentage) return false;
    const vals = col.values.filter((v) => typeof v === "number");
    if (vals.length < 2) return false;
    const sum = vals.reduce((a, v) => a + v, 0);
    return sum >= 80 && sum <= 120 && vals.every((v) => v >= 0 && v <= 100);
  };

  const rowsSumNear100 = () => {
    if (yColumns.length < 2) return false;
    if (!yColumns.every((c) => c.allPercentage)) return false;
    return rows.every((_, ri) => {
      const sum = yColumns.reduce((acc, c) => {
        const v = c.values[ri];
        return acc + (typeof v === "number" ? v : 0);
      }, 0);
      return sum >= 80 && sum <= 120;
    });
  };

  const rowCount = rows.length;
  const yCount = yColumns.length;
  const isTime = xKind === "time";
  const longLabels =
    xKind === "categorical" &&
    xValues.some((v) => String(v).length > 16);

  // ---- Choose default type -------------------------------------------------
  let bestType;
  if (isTime && yCount === 1 && rowCount >= 4) {
    bestType = "area";
  } else if (isTime && yCount > 1) {
    bestType = "line";
  } else if (isTime) {
    bestType = "bar";
  } else if (yCount === 1 && isCompositionSingle(yColumns[0]) && rowCount <= 8) {
    bestType = "donut";
  } else if (yCount > 1 && rowsSumNear100()) {
    bestType = "stackedBar";
  } else if (yCount === 1 && longLabels && rowCount <= 12) {
    bestType = "horizontalBar";
  } else {
    bestType = "bar";
  }

  // ---- Determine which types make sense for this data ---------------------
  const availableTypes = [];
  // Bar / Horizontal bar are always sensible
  availableTypes.push("bar");
  if (rowCount <= 20) availableTypes.push("horizontalBar");

  // Line / Area: require numeric or time x OR ordered categorical
  if (isTime || xKind === "numeric" || rowCount >= 3) {
    availableTypes.push("line");
    availableTypes.push("area");
  }

  // Stacked variants only when there are ≥2 y series
  if (yCount >= 2) {
    availableTypes.push("stackedBar");
    availableTypes.push("stackedArea");
  }

  // Pie/Donut only for single-series compositions (or tiny single series)
  if (yCount === 1 && rowCount >= 2 && rowCount <= 10) {
    if (isCompositionSingle(yColumns[0]) || !isTime) {
      availableTypes.push("donut");
      availableTypes.push("pie");
    }
  }

  // Scatter only for numeric x (not time-as-category)
  if (xKind === "numeric" && yCount >= 1) {
    availableTypes.push("scatter");
  }

  // De-duplicate while preserving order
  const seen = new Set();
  const uniqueAvailable = availableTypes.filter((t) =>
    seen.has(t) ? false : (seen.add(t), true)
  );

  // Always ensure bestType is in the available list
  if (!uniqueAvailable.includes(bestType)) uniqueAvailable.unshift(bestType);

  return {
    xLabel,
    xValues,
    xKind,
    yColumns,
    bestType,
    availableTypes: uniqueAvailable,
  };
};

// ----- Tooltip + legend defaults --------------------------------------------
const baseTooltip = (config) => {
  const fmtForCol = (col) => (val) =>
    fmtNumber(val, { unit: col.unit, magnitude: col.magnitude });
  return {
    trigger: "axis",
    axisPointer: { type: "shadow", shadowStyle: { color: "rgba(107,47,160,0.06)" } },
    backgroundColor: "rgba(17,17,27,0.92)",
    borderWidth: 0,
    textStyle: { color: "#f9fafb", fontFamily: FONT_FAMILY, fontSize: 12 },
    extraCssText: "border-radius:8px; box-shadow:0 6px 20px rgba(0,0,0,0.18);",
    formatter: (params) => {
      if (!Array.isArray(params) || !params.length) return "";
      const head = `<div style="font-weight:600;margin-bottom:4px;">${params[0].axisValueLabel ??
        params[0].name ??
        ""}</div>`;
      const rows = params
        .map((p) => {
          const col =
            config.yColumns.find((c) => c.key === p.seriesName) ||
            config.yColumns[0];
          const valueStr = fmtForCol(col)(p.value);
          return `<div style="display:flex;align-items:center;gap:8px;">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};"></span>
            <span style="opacity:0.85;">${p.seriesName}</span>
            <span style="margin-left:auto;font-weight:600;">${valueStr}</span>
          </div>`;
        })
        .join("");
      return head + rows;
    },
  };
};

const baseLegend = () => ({
  bottom: 0,
  left: "center",
  itemWidth: 10,
  itemHeight: 10,
  icon: "circle",
  textStyle: {
    color: TEXT_COLOR,
    fontFamily: FONT_FAMILY,
    fontSize: 12,
  },
});

const baseGrid = (hasLegend, isHorizontal) => ({
  left: isHorizontal ? 12 : 8,
  right: 16,
  top: 24,
  bottom: hasLegend ? 36 : 24,
  containLabel: true,
});

// ----- Build ECharts option --------------------------------------------------
export const buildEchartsOption = (config, type) => {
  if (!config) return null;
  const { xLabel, xValues, yColumns, xKind } = config;
  if (!yColumns.length) return null;

  const requestedType = type || config.bestType;
  // Coerce unsupported types to nearest supported.
  const t = config.availableTypes.includes(requestedType)
    ? requestedType
    : config.bestType;

  // ---- Pie / Donut -------------------------------------------------------
  if (t === "pie" || t === "donut") {
    const seriesCol = yColumns[0];
    const data = xValues
      .map((x, idx) => ({ name: String(x), value: seriesCol.values[idx] }))
      .filter((d) => typeof d.value === "number" && !Number.isNaN(d.value));
    const radius = t === "donut" ? ["48%", "72%"] : [0, "70%"];
    return {
      color: CHART_PALETTE,
      textStyle: { fontFamily: FONT_FAMILY, color: TEXT_COLOR },
      tooltip: {
        trigger: "item",
        backgroundColor: "rgba(17,17,27,0.92)",
        borderWidth: 0,
        textStyle: { color: "#f9fafb", fontFamily: FONT_FAMILY, fontSize: 12 },
        extraCssText:
          "border-radius:8px; box-shadow:0 6px 20px rgba(0,0,0,0.18);",
        formatter: (p) =>
          `<div style="font-weight:600;margin-bottom:2px;">${p.name}</div>
           <div>${fmtNumber(p.value, {
             unit: seriesCol.unit,
             magnitude: seriesCol.magnitude,
           })} <span style="opacity:0.7;">(${p.percent}%)</span></div>`,
      },
      legend: {
        ...baseLegend(),
        bottom: 0,
        type: "scroll",
      },
      series: [
        {
          name: seriesCol.key,
          type: "pie",
          radius,
          center: ["50%", "46%"],
          avoidLabelOverlap: true,
          label: {
            color: TEXT_COLOR,
            fontFamily: FONT_FAMILY,
            fontSize: 12,
            formatter: "{b}: {d}%",
          },
          labelLine: { length: 8, length2: 6 },
          itemStyle: {
            borderColor: "#fff",
            borderWidth: 2,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 14,
              shadowOffsetX: 0,
              shadowColor: "rgba(107,47,160,0.25)",
            },
          },
          data,
        },
      ],
    };
  }

  // ---- Scatter -----------------------------------------------------------
  if (t === "scatter") {
    const numericX = xValues.map((v) => parseFloat(String(v).replace(/,/g, "")));
    const series = yColumns.map((col, i) => ({
      name: col.key,
      type: "scatter",
      symbolSize: 10,
      data: col.values.map((v, idx) => [numericX[idx], v]),
      itemStyle: {
        color: CHART_PALETTE[i % CHART_PALETTE.length],
        opacity: 0.85,
      },
    }));
    return {
      color: CHART_PALETTE,
      textStyle: { fontFamily: FONT_FAMILY, color: TEXT_COLOR },
      tooltip: baseTooltip(config),
      legend: yColumns.length > 1 ? baseLegend() : undefined,
      grid: baseGrid(yColumns.length > 1, false),
      xAxis: {
        type: "value",
        name: xLabel,
        nameGap: 24,
        nameTextStyle: { color: AXIS_COLOR, fontSize: 11, padding: [0, 0, 0, 0] },
        axisLine: { lineStyle: { color: SPLIT_COLOR } },
        axisTick: { show: false },
        axisLabel: { color: AXIS_COLOR, fontSize: 11 },
        splitLine: { lineStyle: { color: SPLIT_COLOR } },
      },
      yAxis: {
        type: "value",
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: AXIS_COLOR,
          fontSize: 11,
          formatter: axisLabelFormatter(yColumns[0].unit, yColumns[0].magnitude),
        },
        splitLine: { lineStyle: { color: SPLIT_COLOR } },
      },
      series,
    };
  }

  // ---- Cartesian (bar / line / area / stacked variants / horizontal) -----
  const isHorizontal = t === "horizontalBar";
  const isLine = t === "line" || t === "area" || t === "stackedArea";
  const stack =
    t === "stackedBar" || t === "stackedArea" ? "total" : undefined;

  const series = yColumns.map((col, i) => {
    const color = CHART_PALETTE[i % CHART_PALETTE.length];
    const isAreaType = t === "area" || t === "stackedArea";

    if (isLine) {
      return {
        name: col.key,
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 6,
        showSymbol: xValues.length <= 12,
        lineStyle: { width: 2.5, color },
        itemStyle: { color },
        stack,
        emphasis: { focus: "series", lineStyle: { width: 3 } },
        areaStyle: isAreaType
          ? {
              opacity: 0.18,
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: color + "AA" },
                  { offset: 1, color: color + "11" },
                ],
              },
            }
          : undefined,
        data: col.values,
      };
    }

    // Bar / horizontal bar
    return {
      name: col.key,
      type: "bar",
      stack,
      barMaxWidth: 28,
      itemStyle: {
        color,
        borderRadius: isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0],
      },
      emphasis: { focus: "series" },
      data: col.values,
    };
  });

  const showLegend = yColumns.length > 1;
  const useDataZoom = !isHorizontal && xValues.length > 16;

  // For axis label formatting, prefer the dominant y column's unit/magnitude.
  const primaryCol = yColumns[0];
  const valueAxisLabel = {
    color: AXIS_COLOR,
    fontSize: 11,
    formatter: axisLabelFormatter(primaryCol.unit, primaryCol.magnitude),
  };

  const categoryAxis = {
    type: "category",
    data: xValues,
    name: xKind === "categorical" ? undefined : xLabel,
    axisLine: { lineStyle: { color: SPLIT_COLOR } },
    axisTick: { show: false },
    axisLabel: {
      color: AXIS_COLOR,
      fontSize: 11,
      interval: isHorizontal ? 0 : "auto",
      rotate: !isHorizontal && xValues.length > 6 ? 28 : 0,
      hideOverlap: true,
    },
    splitLine: { show: false },
  };

  const valueAxis = {
    type: "value",
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: valueAxisLabel,
    splitLine: { lineStyle: { color: SPLIT_COLOR } },
  };

  return {
    color: CHART_PALETTE,
    textStyle: { fontFamily: FONT_FAMILY, color: TEXT_COLOR },
    animationDuration: 600,
    animationEasing: "cubicOut",
    tooltip: baseTooltip(config),
    legend: showLegend ? baseLegend() : undefined,
    grid: baseGrid(showLegend, isHorizontal),
    xAxis: isHorizontal ? valueAxis : categoryAxis,
    yAxis: isHorizontal
      ? { ...categoryAxis, inverse: true }
      : valueAxis,
    dataZoom: useDataZoom
      ? [
          {
            type: "inside",
            start: 0,
            end: Math.min(100, (12 / xValues.length) * 100),
          },
          {
            type: "slider",
            height: 14,
            bottom: showLegend ? 24 : 6,
            borderColor: "transparent",
            backgroundColor: "#f4f4f5",
            fillerColor: "rgba(107,47,160,0.18)",
            handleStyle: { color: "#6B2FA0" },
            moveHandleStyle: { color: "#6B2FA0" },
            textStyle: { color: AXIS_COLOR, fontSize: 10 },
          },
        ]
      : undefined,
    series,
  };
};

// ----- Display labels for chart types ----------------------------------------
export const CHART_TYPE_LABELS = {
  bar: "Bar",
  horizontalBar: "Horizontal",
  line: "Line",
  area: "Area",
  stackedBar: "Stacked",
  stackedArea: "Stacked Area",
  pie: "Pie",
  donut: "Donut",
  scatter: "Scatter",
};
